/**
 * Pricing Rules Page (New or Used vehicles)
 * Frontend-only page using existing endpoints:
 *   GET  /unlock/pricing-rules
 *   POST /unlock/pricing-rules
 *   DELETE /unlock/pricing-rules/{id}
 *
 * Rule encoding (backward-compatible with existing VD_SMART scheme):
 *   rule_type: "vin_override"
 *   vin: "VD_RULE:{condition}|{year}|{make}|{model}|{trim}|{based_on}|{incentive}"
 *   unlock_price: "$500 off" or "10% off"
 *
 * This page reads ALL rules and filters to ones matching `condition` ("new" or "used").
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Plus, Edit2, Trash2, DollarSign, Percent, ArrowLeft, X, Lock, AlertCircle } from "lucide-react";
import franchiseMakesModels from "../data/franchise_makes_models.json";
import TableScroll from "../components/TableScroll";

const RULE_PREFIX = "VD_RULE:";
const BASED_ON_OPTIONS = [
  { value: "sale_price", label: "Sale Price" },
  { value: "msrp", label: "MSRP" },
];
// iter88 — Issue 11: derive YEAR_OPTIONS dynamically so the "Add Rule"
// dialog defaults to the current calendar year. Previously hard-coded
// to ["2025","2026","2027"], which left dealers staring at a 2025
// default in 2026 (and would be even more stale in 2027+). We show
// last year (close-out inventory) → current year → next year (early
// model arrivals), which is the practical range a dealer touches.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [String(CURRENT_YEAR - 1), String(CURRENT_YEAR), String(CURRENT_YEAR + 1)];

const encodeRuleVin = ({ condition, year, make, model, based_on, incentive }) =>
  `${RULE_PREFIX}${condition || ""}|${year || ""}|${make || ""}|${model || ""}||${based_on || ""}|${incentive || ""}`;

const decodeRuleVin = (vin) => {
  if (!vin || !vin.startsWith(RULE_PREFIX)) return null;
  const [condition, year, make, model, trim, based_on, incentive] = vin.replace(RULE_PREFIX, "").split("|");
  return { condition, year, make, model, trim, based_on, incentive };
};

const PricingRulesPage = ({ condition, businessId, title: titleOverride, compact = false }) => {
  if (condition === "used") {
    return <UsedPricingSimple businessId={businessId} compact={compact} titleOverride={titleOverride} />;
  }
  return <NewPricingRules condition={condition} businessId={businessId} titleOverride={titleOverride} compact={compact} />;
};

const NewPricingRules = ({ condition, businessId, titleOverride, compact }) => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Make/Model catalog from demand analytics — used to populate dropdowns
  const [catalog, setCatalog] = useState([]); // [{year, make, model, trim}]
  const [incentives, setIncentives] = useState([]);
  // Dealer's franchise make — locks Make dropdown for New vehicle rules
  const [franchiseMake, setFranchiseMake] = useState("");
  // iter130b — surface the global Default Price (settings.unlock_price)
  // on the pricing-rules page so dealers can see and clear the fallback
  // without having to hunt for it on the Unlock Settings page.
  const [defaultUnlockPrice, setDefaultUnlockPrice] = useState(null);
  const [clearingDefault, setClearingDefault] = useState(false);

  const conditionLabel = condition === "new" ? "New" : "Used";

  // Build a query suffix to scope requests to a specific dealer when a master
  // admin is viewing a dealer's detail page. Dealer/self view leaves it blank.
  const bizQuery = businessId ? `business_id=${encodeURIComponent(businessId)}` : "";
  const withBiz = (url) => {
    if (!bizQuery) return url;
    return url.includes("?") ? `${url}&${bizQuery}` : `${url}?${bizQuery}`;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesRes, vehiclesRes, franchiseRes] = await Promise.all([
        apiFetch(withBiz("/unlock/pricing-rules"), { method: "GET" }),
        apiFetch(withBiz("/analytics/top-vehicles?days=90&metric=views&limit=50")).catch(() => ({ data: { items: [] } })),
        // Master admin context: fetch franchise_make from admin endpoint scoped by businessId.
        // Dealer/self view: fetch from /settings/business.
        businessId
          ? apiFetch(`/admin/dealers/${businessId}`).catch(() => ({ data: {} }))
          : apiFetch("/settings/business").catch(() => ({ data: {} })),
      ]);
      const all = rulesRes.data?.rules || [];
      const filtered = all
        .map(r => ({ ...r, meta: decodeRuleVin(r.vin) }))
        .filter(r => r.meta && r.meta.condition === condition);
      setRules(filtered);

      const items = vehiclesRes.data?.items || [];
      setCatalog(items.map(v => ({
        year: v.year, make: v.make, model: v.model, trim: v.trim,
      })).filter(v => v.make));

      setFranchiseMake(franchiseRes.data?.franchise_make || "");

      // iter130b — also load the dealer's Unlock Settings so we can show
      // a fallback warning when settings.unlock_price is set. Dealers
      // reported the global Default Price was invisible from this
      // page and they couldn't figure out where the cross-vehicle
      // discount was leaking from.
      try {
        const settingsRes = await apiFetch(withBiz("/unlock/settings"), { method: "GET" });
        setDefaultUnlockPrice(settingsRes.data?.unlock_price || null);
      } catch { /* settings optional — banner just won't render */ }

      // Incentive programs — graceful failure if endpoint not available
      try {
        const incRes = await apiFetch(withBiz("/unlock/pricing-rules/incentive-programs"), { method: "GET" });
        setIncentives(incRes.data?.programs || []);
      } catch { /* endpoint optional */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pricing rules");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch, condition, toast, businessId]);

  useEffect(() => { loadData(); }, [loadData]);

  const makes = useMemo(() => {
    const s = new Set(catalog.map(v => v.make).filter(Boolean));
    return Array.from(s).sort();
  }, [catalog]);

  const openAdd = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  // iter81 — open the same 2-step modal pre-filled from an existing
  // rule. The rule's encoded VIN (VD_RULE:…) has already been
  // decoded into `r.meta` (year/make/model/based_on/incentive); the
  // discount lives in `r.unlock_price` as "$X off" / "X% off" / "$X".
  // We split that string back into {discount_amount, discount_type}
  // and stash the original id on `editingRule.id` so handleSaveRule
  // knows to PUT instead of POST.
  const parseDiscountString = (s) => {
    if (!s) return { discount_amount: "", discount_type: "dollars" };
    const str = String(s).trim();
    const isPct = str.includes("%");
    const numMatch = str.match(/(\d+(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)/);
    const num = numMatch ? numMatch[1].replace(/,/g, "") : "";
    return {
      discount_amount: num,
      discount_type: isPct ? "percent" : "dollars",
    };
  };

  const openEdit = (rule) => {
    const { discount_amount, discount_type } = parseDiscountString(rule.unlock_price);
    setEditingRule({
      id: rule.id,
      year: rule.meta?.year || "",
      make: rule.meta?.make || "",
      model: rule.meta?.model || "",
      based_on: rule.meta?.based_on || "sale_price",
      incentive: rule.meta?.incentive || "",
      discount_amount,
      discount_type,
    });
    setShowModal(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm("Delete this pricing rule?")) return;
    try {
      await apiFetch(withBiz(`/unlock/pricing-rules/${ruleId}`), { method: "DELETE" });
      setRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success("Rule deleted");
    } catch (e) {
      toast.error("Failed to delete rule");
    }
  };

  const handleSaveRule = async (data) => {
    setSaving(true);
    try {
      const formatted = data.discount_type === "dollars"
        ? `$${data.discount_amount} off`
        : `${data.discount_amount}% off`;
      const vin = encodeRuleVin({
        condition,
        year: data.year,
        make: data.make,
        model: data.model,
        based_on: data.based_on,
        incentive: data.incentive,
      });
      // iter81 — PUT on edit, POST on create. The PUT endpoint at
      // /api/unlock/pricing-rules/{id} updates vin + unlock_price +
      // priority in place; we merge the result locally so the row
      // refreshes without a full reload.
      if (editingRule && editingRule.id) {
        await apiFetch(withBiz(`/unlock/pricing-rules/${editingRule.id}`), {
          method: "PUT",
          data: { vin, unlock_price: formatted, priority: 9 },
        });
        setRules(prev => prev.map(r => r.id === editingRule.id
          ? { ...r, vin, unlock_price: formatted, meta: decodeRuleVin(vin) }
          : r
        ));
        setShowModal(false);
        setEditingRule(null);
        toast.success("Pricing rule updated");
      } else {
        const res = await apiFetch(withBiz("/unlock/pricing-rules"), {
          method: "POST",
          data: {
            rule_type: "vin_override",
            vin,
            unlock_price: formatted,
            priority: 9,
          },
        });
        const newRule = { ...res.data.rule, meta: decodeRuleVin(res.data.rule.vin) };
        setRules(prev => [newRule, ...prev]);
        setShowModal(false);
        toast.success("Pricing rule added");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  // Gate the entire New Vehicle Pricing Rules page if no franchise_make is set
  // (only applies to condition=new — used has its own simple form).
  const franchiseGateActive = condition === "new" && !loading && !franchiseMake;

  return (
    <div
      className={compact ? "" : "p-6 w-full"}
      data-testid={`pricing-rules-${condition}-page`}
    >
      {!compact && (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {titleOverride || `Pricing Rules — ${conditionLabel} Vehicles`}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5 whitespace-normal break-words">
              Rules applied to {conditionLabel.toLowerCase()} vehicle unlocks. More specific rules win (Trim &gt; Model &gt; Make).
            </p>
          </div>
          {!franchiseGateActive && (
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="add-pricing-rule-btn"
            >
              <Plus size={16} /> Add Rule
            </button>
          )}
        </div>
      )}
      {compact && !franchiseGateActive && (
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors"
            data-testid="add-pricing-rule-btn"
          >
            <Plus size={14} /> Add Rule
          </button>
        </div>
      )}

      {/* iter130b — Default Price (settings.unlock_price) fallback banner.
          When set, this discount is applied to EVERY vehicle that doesn't
          match a more specific rule below — including non-Ford / non-F-150
          / used vehicles. Surfacing it here so dealers don't get surprised
          when their "F-150 only" rule appears to also discount Mustangs,
          Explorers, BMWs, etc. */}
      {defaultUnlockPrice && !franchiseGateActive && (
        <div
          data-testid="default-unlock-price-banner"
          className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="flex items-start gap-3 min-w-0">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                Default Price fallback: <span data-testid="default-unlock-price-value">{defaultUnlockPrice}</span>
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Applied to every vehicle that doesn&apos;t match a rule below — including used cars, other makes, and other models. Clear it if you only want the rules below to apply.
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="clear-default-unlock-price-btn"
            disabled={clearingDefault}
            onClick={async () => {
              if (!window.confirm(
                "Clear the Default Price fallback?\n\n" +
                `Currently: ${defaultUnlockPrice}\n\n` +
                "After clearing, vehicles that don't match any pricing rule will show the unlock CTA WITHOUT a discount (e.g. \"Call for Best Price\"). Specific rules below stay intact."
              )) return;
              setClearingDefault(true);
              try {
                await apiFetch(withBiz("/unlock/settings"), {
                  method: "PUT",
                  data: { unlock_price: null },
                });
                setDefaultUnlockPrice(null);
                toast.success("Default Price cleared. Only specific rules below will apply now.");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to clear Default Price");
              } finally {
                setClearingDefault(false);
              }
            }}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <X size={14} /> {clearingDefault ? "Clearing…" : "Clear fallback"}
          </button>
        </div>
      )}

      {franchiseGateActive ? (
        <div
          className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center"
          data-testid="pricing-new-franchise-gate"
        >
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 text-amber-700 mb-3">
            <Lock size={20} />
          </div>
          <h3 className="text-base font-semibold text-amber-900">
            New Vehicle Pricing Rules locked
          </h3>
          <p className="text-sm text-amber-800 mt-2 max-w-lg mx-auto">
            Please set this dealer&apos;s Franchise Make in the Overview tab before adding new vehicle pricing rules.
          </p>
        </div>
      ) : (
        <>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <TableScroll testId={`pricing-rules-${condition}-scroll`}>
          <table className="w-full text-sm" data-testid={`pricing-rules-${condition}-table`}>
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase text-gray-500 tracking-wide bg-gray-50">
                <th className="py-3 px-4">Year</th>
                <th className="py-3 px-4">Make</th>
                <th className="py-3 px-4">Model</th>
                {/* iter91 — Issue 5: Based On + Incentives deprioritised
                    at <lg viewports. The discount column still shows
                    the resulting price so dealers have the actionable
                    info; Based On is meta-context they can see on
                    edit. */}
                <th className="py-3 px-4 hide-lg">Based On</th>
                <th className="py-3 px-4">Discount</th>
                <th className="py-3 px-4 hide-lg">Incentives</th>
                <th className="py-3 px-4 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-10 text-center text-gray-400">Loading…</td></tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <p className="text-gray-500 font-medium">No {conditionLabel.toLowerCase()} vehicle pricing rules yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click &quot;Add Rule&quot; to create your first rule.</p>
                  </td>
                </tr>
              ) : (
                rules.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`pricing-rule-row-${r.id}`}>
                    <td className="py-3 px-4 text-gray-800">{r.meta.year || <span className="text-gray-400">All</span>}</td>
                    <td className="py-3 px-4 font-medium text-gray-900">{r.meta.make || <span className="text-gray-400">Any</span>}</td>
                    <td className="py-3 px-4 text-gray-700">{r.meta.model || <span className="text-gray-400">Any</span>}</td>
                    <td className="py-3 px-4 text-gray-600 hide-lg">{BASED_ON_OPTIONS.find(o => o.value === r.meta.based_on)?.label || "—"}</td>
                    <td className="py-3 px-4 font-semibold text-emerald-600">{r.unlock_price}</td>
                    <td className="py-3 px-4 text-gray-600 hide-lg">{r.meta.incentive || <span className="text-gray-400">None</span>}</td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(r)}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="Edit"
                          data-testid={`edit-rule-${r.id}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                          data-testid={`delete-rule-${r.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableScroll>
      </div>

      {showModal && (
        <AddRuleModal
          conditionLabel={conditionLabel}
          condition={condition}
          catalog={catalog}
          makes={makes}
          incentives={incentives}
          franchiseMake={franchiseMake}
          saving={saving}
          editingRule={editingRule}
          onClose={() => { setShowModal(false); setEditingRule(null); }}
          onSave={handleSaveRule}
        />
      )}
        </>
      )}
    </div>
  );
};

// ================= 2-Step Add/Edit Rule Modal =================
const AddRuleModal = ({ conditionLabel, condition, catalog, makes, incentives, franchiseMake, saving, editingRule, onClose, onSave }) => {
  const isNewCondition = condition === "new";
  const franchiseLocked = isNewCondition && !!franchiseMake;
  const missingFranchise = isNewCondition && !franchiseMake;
  const isEditing = !!(editingRule && editingRule.id);

  // iter81 — seed state from editingRule when present so the modal
  // opens pre-filled with the existing rule's Year/Make/Model/Based On/
  // Discount amount/Discount type/Incentive. New rules keep the
  // previous defaults.
  const [step, setStep] = useState(1);
  // iter88 — Issue 11: default to the current calendar year, not the
  // legacy hard-coded first entry. YEAR_OPTIONS is [last, current, next]
  // so index 1 is the current year.
  const [year, setYear] = useState(editingRule?.year || String(CURRENT_YEAR));
  const [make, setMake] = useState(editingRule?.make || (franchiseLocked ? franchiseMake : ""));
  const [model, setModel] = useState(editingRule?.model || "");

  const [basedOn, setBasedOn] = useState(editingRule?.based_on || "sale_price");
  const [discountAmount, setDiscountAmount] = useState(editingRule?.discount_amount || "");
  const [discountType, setDiscountType] = useState(editingRule?.discount_type || "dollars");
  const [incentive, setIncentive] = useState(editingRule?.incentive || "");

  // When New condition: models are sourced from franchise_makes_models.json instead of demand data
  const models = useMemo(() => {
    if (franchiseLocked) {
      return franchiseMakesModels[franchiseMake] || [];
    }
    if (!make) return [];
    const s = new Set(catalog.filter(v => v.make === make).map(v => v.model).filter(Boolean));
    return Array.from(s).sort();
  }, [catalog, make, franchiseLocked, franchiseMake]);

  const canNext = make !== "" && !missingFranchise;
  const canDone = discountAmount !== "" && !isNaN(Number(discountAmount));

  const summary = [year, make, model].filter(Boolean).join(" ") || "All vehicles";

  const handleDone = () => {
    onSave({
      year, make, model,
      based_on: basedOn,
      discount_amount: discountAmount,
      discount_type: discountType,
      incentive,
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="add-rule-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Step {step} of 2</p>
            <h3 className="text-base font-semibold text-gray-900 mt-0.5">
              {step === 1
                ? `${isEditing ? "Edit" : "Add"} Rule — ${conditionLabel} Vehicles`
                : "Setup Pricing"}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 rounded" data-testid="close-rule-modal">
            <X size={18} />
          </button>
        </div>

        {step === 1 ? (
          <div className="p-6 space-y-4">
            {missingFranchise && (
              <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="missing-franchise-warning">
                <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">Franchise Make required</p>
                  <p className="mt-1 text-amber-800">
                    Set your Franchise Make in <span className="font-medium">General Info</span> before creating New vehicle pricing rules.
                  </p>
                </div>
              </div>
            )}

            <Select label="Year" value={year} onChange={setYear} options={YEAR_OPTIONS} testId="rule-year" />

            {franchiseLocked ? (
              <div>
                <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">Make</span>
                <div className="relative">
                  <input
                    type="text"
                    value={franchiseMake}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                    data-testid="rule-make"
                  />
                  <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Locked to your franchise make. Edit it in General Info.</p>
              </div>
            ) : (
              <Autocomplete
                label="Make *"
                value={make}
                onChange={(v) => { setMake(v); setModel(""); }}
                options={makes}
                placeholder="Type or select a make"
                listId="rule-make-list"
                testId="rule-make"
                disabled={missingFranchise}
              />
            )}

            {franchiseLocked ? (
              <Select
                label="Model *"
                value={model}
                onChange={setModel}
                options={["", ...models]}
                placeholder={models.length ? "Select a model" : "No models available"}
                testId="rule-model"
              />
            ) : (
              <Autocomplete
                label="Model"
                value={model}
                onChange={setModel}
                options={models}
                disabled={!make || missingFranchise}
                placeholder={make ? "Type or select a model" : "Select a make first"}
                listId="rule-model-list"
                testId="rule-model"
              />
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg" data-testid="rule-cancel-step1">
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!canNext}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="rule-next-btn"
              >
                Next
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1">Rule applies to</p>
              <p className="text-sm font-medium text-gray-900" data-testid="rule-summary">{summary}</p>
            </div>

            <Select
              label="Based On"
              value={basedOn}
              onChange={setBasedOn}
              options={BASED_ON_OPTIONS.map(o => o.value)}
              labelMap={Object.fromEntries(BASED_ON_OPTIONS.map(o => [o.value, o.label]))}
              testId="rule-based-on"
            />

            <div>
              <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">Discount</span>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="e.g. 500"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="rule-discount-amount"
                />
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDiscountType("dollars")}
                    className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${discountType === "dollars" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    data-testid="rule-discount-dollars"
                  >
                    <DollarSign size={14} /> $
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountType("percent")}
                    className={`px-3 py-2 text-sm font-medium flex items-center gap-1 border-l border-gray-300 ${discountType === "percent" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    data-testid="rule-discount-percent"
                  >
                    <Percent size={14} /> %
                  </button>
                </div>
              </div>
            </div>

            <div>
              <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">Incentives</span>
              <select
                value={incentive}
                onChange={(e) => setIncentive(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="rule-incentive"
              >
                <option value="">None</option>
                {incentives.map(p => (
                  <option key={p.id || p.name} value={p.name || p.id}>{p.name}</option>
                ))}
              </select>
              {incentives.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">Incentive programs coming soon.</p>
              )}
            </div>

            <div className="flex justify-between pt-4 border-t border-gray-100">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-1 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg" data-testid="rule-back-btn">
                <ArrowLeft size={14} /> Back
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg" data-testid="rule-cancel-step2">
                  Cancel
                </button>
                <button
                  onClick={handleDone}
                  disabled={!canDone || saving}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="rule-done-btn"
                >
                  {saving ? "Saving…" : "Done"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Select = ({ label, value, onChange, options, disabled, placeholder, labelMap, testId }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-50 disabled:text-gray-400"
      data-testid={testId}
    >
      {options.map((opt) => (
        <option key={opt || "__empty"} value={opt}>
          {opt === "" ? (placeholder || "Select…") : (labelMap ? labelMap[opt] : opt)}
        </option>
      ))}
    </select>
  </label>
);

// Combobox-style input backed by a <datalist> of suggestions.
// Users can either pick a value from the dropdown (sourced from /analytics/top-vehicles)
// or type a custom value when the catalog doesn't include it yet.
const Autocomplete = ({ label, value, onChange, options, disabled, placeholder, listId, testId }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">{label}</span>
    <input
      type="text"
      list={listId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      autoComplete="off"
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-50 disabled:text-gray-400"
      data-testid={testId}
    />
    <datalist id={listId}>
      {options.filter(Boolean).map((opt) => (
        <option key={opt} value={opt} />
      ))}
    </datalist>
  </label>
);

// ================= Used Vehicles — Simple Flat Discount =================
// One rule applies across ALL used inventory. No vehicle-specific rules.
// Persisted as rule_type="condition" + condition="used" in unlock_pricing_rules.
const UsedPricingSimple = ({ businessId, compact, titleOverride }) => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ruleId, setRuleId] = useState(null);
  const [amount, setAmount] = useState("");
  const [discountType, setDiscountType] = useState("dollars");
  // iter130b — surface the global Default Price (settings.unlock_price)
  // here too. Without this banner, dealers can't tell that leaving the
  // used-discount blank still results in non-zero discounts on used
  // cars (because the global fallback fires for any non-matching rule).
  const [defaultUnlockPrice, setDefaultUnlockPrice] = useState(null);
  const [clearingDefault, setClearingDefault] = useState(false);

  const bizQuery = businessId ? `business_id=${encodeURIComponent(businessId)}` : "";
  const withBiz = (url) => {
    if (!bizQuery) return url;
    return url.includes("?") ? `${url}&${bizQuery}` : `${url}?${bizQuery}`;
  };

  const parsePrice = (priceStr) => {
    if (!priceStr) return { amount: "", type: "dollars" };
    const pct = priceStr.match(/^(\d+(?:\.\d+)?)%/);
    if (pct) return { amount: pct[1], type: "percent" };
    const dol = priceStr.match(/\$(\d+(?:\.\d+)?)/);
    if (dol) return { amount: dol[1], type: "dollars" };
    return { amount: "", type: "dollars" };
  };

  const loadExisting = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesRes, settingsRes] = await Promise.all([
        apiFetch(withBiz("/unlock/pricing-rules"), { method: "GET" }),
        apiFetch(withBiz("/unlock/settings"), { method: "GET" }).catch(() => ({ data: {} })),
      ]);
      const all = rulesRes.data?.rules || [];
      const existing = all.find(r => r.rule_type === "condition" && r.condition === "used");
      if (existing) {
        setRuleId(existing.id);
        const { amount: amt, type } = parsePrice(existing.unlock_price);
        setAmount(amt);
        setDiscountType(type);
      } else {
        setRuleId(null);
        setAmount("");
        setDiscountType("dollars");
      }
      setDefaultUnlockPrice(settingsRes.data?.unlock_price || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load used vehicle discount");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch, toast, businessId]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const canSave = (() => {
    if (amount === "" || isNaN(Number(amount)) || Number(amount) <= 0) return false;
    // iter83 — Bug 8: cap percent at 100. A 150% discount is non-
    // sensical and breaks the unlock price math. Flat $ amounts have
    // no cap (high-end vehicles can legitimately have $5000+ off).
    if (discountType === "percent" && Number(amount) > 100) return false;
    return true;
  })();
  const percentInvalid = discountType === "percent" && Number(amount) > 100;

  const handleSave = async () => {
    setSaving(true);
    try {
      const formatted = discountType === "dollars" ? `$${amount} off` : `${amount}% off`;
      // Delete old then create new (simpler than PUT). Backend supports both.
      if (ruleId) {
        await apiFetch(withBiz(`/unlock/pricing-rules/${ruleId}`), { method: "DELETE" });
      }
      const res = await apiFetch(withBiz("/unlock/pricing-rules"), {
        method: "POST",
        data: {
          rule_type: "condition",
          condition: "used",
          unlock_price: formatted,
          priority: 5,
        },
      });
      setRuleId(res.data?.rule?.id || null);
      toast.success("Used vehicle discount saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save discount");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!ruleId) return;
    if (!window.confirm("Remove the used vehicle discount?")) return;
    try {
      await apiFetch(withBiz(`/unlock/pricing-rules/${ruleId}`), { method: "DELETE" });
      setRuleId(null);
      setAmount("");
      toast.success("Discount removed");
    } catch (e) {
      toast.error("Failed to remove discount");
    }
  };

  return (
    <div
      className={compact ? "" : "p-6 max-w-[720px] mx-auto"}
      data-testid="pricing-rules-used-page"
    >
      {!compact && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {titleOverride || "Pricing Rules — Used Vehicles"}
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            A single discount applied to every used vehicle on your lot.
          </p>
        </div>
      )}

      {/* iter130b — Default Price fallback banner. Same UX as the New
          Vehicles page so dealers see what's leaking onto their used
          inventory when no used-specific rule exists. */}
      {defaultUnlockPrice && (
        <div
          data-testid="default-unlock-price-banner-used"
          className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="flex items-start gap-3 min-w-0">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                Default Price fallback: <span data-testid="default-unlock-price-value-used">{defaultUnlockPrice}</span>
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                If you leave the used discount blank below, every used vehicle still gets this fallback discount. Clear it to make used vehicles show no discount until you set one explicitly.
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="clear-default-unlock-price-btn-used"
            disabled={clearingDefault}
            onClick={async () => {
              if (!window.confirm(
                "Clear the Default Price fallback?\n\n" +
                `Currently: ${defaultUnlockPrice}\n\n` +
                "After clearing, vehicles that don't match any pricing rule will show the unlock CTA WITHOUT a discount. Specific rules stay intact."
              )) return;
              setClearingDefault(true);
              try {
                await apiFetch(withBiz("/unlock/settings"), {
                  method: "PUT",
                  data: { unlock_price: null },
                });
                setDefaultUnlockPrice(null);
                toast.success("Default Price cleared.");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to clear Default Price");
              } finally {
                setClearingDefault(false);
              }
            }}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <X size={14} /> {clearingDefault ? "Clearing…" : "Clear fallback"}
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="used-discount-card">
        <div className="flex items-center gap-2 pb-3 mb-5 border-b border-gray-100">
          <DollarSign size={16} className="text-purple-600" />
          <h2 className="text-sm font-semibold text-gray-900">Used Vehicle Discount</h2>
          {ruleId && !loading && (
            <span className="ml-auto inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px] font-semibold" data-testid="used-discount-active-badge">
              Active
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-gray-600">
              This discount applies to every used vehicle in your inventory. Enter an amount and choose whether it&apos;s a flat dollar off or a percentage off.
            </p>

            <div>
              <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">Discount</span>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max={discountType === "percent" ? "100" : undefined}
                  step={discountType === "dollars" ? "1" : "0.1"}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder={discountType === "dollars" ? "e.g. 500" : "e.g. 10"}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${percentInvalid ? "border-red-400" : "border-gray-300"}`}
                  data-testid="used-discount-amount"
                />
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDiscountType("dollars")}
                    className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${discountType === "dollars" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    data-testid="used-discount-dollars-toggle"
                  >
                    <DollarSign size={14} /> Flat $
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountType("percent")}
                    className={`px-3 py-2 text-sm font-medium flex items-center gap-1 border-l border-gray-300 ${discountType === "percent" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    data-testid="used-discount-percent-toggle"
                  >
                    <Percent size={14} /> Percent
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Preview: {canSave ? (discountType === "dollars" ? `$${amount} off every used vehicle` : `${amount}% off every used vehicle`) : "Enter a discount to see preview"}
              </p>
              {percentInvalid && (
                <p className="text-xs text-red-600 mt-1" data-testid="used-discount-percent-error">
                  Percent discount must be between 0 and 100.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              {ruleId ? (
                <button
                  onClick={handleRemove}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid="used-discount-remove-btn"
                >
                  <Trash2 size={14} /> Remove Discount
                </button>
              ) : (
                <span />
              )}
              <button
                onClick={handleSave}
                disabled={!canSave || saving}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="used-discount-save-btn"
              >
                {saving ? "Saving…" : ruleId ? "Update Discount" : "Save Discount"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingRulesPage;
